// The terrain (the suggestion "heightfield of authored landmarks over noise; one height function, three users"): one
// height function built from the layout and the seed, which the physics' heightfields, the meshes, the scatter and
// the placements all sample, so they never disagree. Layered in order:
//   1. noise layers (each its own sub-seed), scaled by each region's roughness;
//   2. the valley's walls, rising inside the bounds; landmarks (rises and bowls);
//   3. regions with `flatten` levelled toward their centre's height (a town's flat);
//   4. paths levelled toward their own line's height (smoothed along the path), with shoulders;
//   5. water carved last: a stream's bed follows its course downhill, a pond's bowl sits under its surface.
// Surface tags come from the same layout: water's bed and bank, a path, a cliff (steep ground), the topmost region,
// else the terrain's default.
import { fbm } from "./noise";
import { boxDistance, nearestOn, pointAlong, polyline, shapeBox, shapeCentre, shapeDistance, smoothstep, type Polyline } from "./geom";
import type { Layout, Water } from "./layout";
import { subSeed } from "./seeds";

/** A stream's course as the terrain carved it: points every couple of metres with the bed's and the water surface's
 *  heights (the water seam: the game makes the water volume and its look from these). */
export interface StreamCourse {
  id: string;
  kind: "stream";
  width: number;
  points: { x: number; z: number; bed: number; surface: number }[];
}
/** A pond as carved: its circle, the bed's lowest point and the water surface. */
export interface PondCourse {
  id: string;
  kind: "pond";
  x: number;
  z: number;
  r: number;
  bed: number;
  surface: number;
}
export type WaterCourse = StreamCourse | PondCourse;

/** The terrain of one layout and seed. */
export interface Terrain {
  /** The ground's height (m) at (x, z). */
  heightAt(x: number, z: number): number;
  /** The ground's slope (degrees) at (x, z), from the height function's differences. */
  slopeAt(x: number, z: number): number;
  /** The ground's surface tag at (x, z). */
  surfaceAt(x: number, z: number): string;
  /** The topmost region containing (x, z), or null. */
  regionAt(x: number, z: number): string | null;
  /** How far (m) (x, z) is from the nearest path's edge (negative on it), and which path. */
  pathAt(x: number, z: number): { id: string; d: number } | null;
  /** How far (m) (x, z) is from the nearest water's edge (negative in it), and which. */
  waterAt(x: number, z: number): { id: string; d: number; surface: number } | null;
  /** The water courses as carved. */
  water(): WaterCourse[];
}

const SHOULDER = 3; // m of a path's levelling past its edge
const PATH_SMOOTH = 4; // m along the path the levelling height averages over
const STREAM_SMOOTH = 10; // m along a stream its bank height averages over
const STREAM_STEP = 2; // m between a stream's carved samples

/** Build a layout's terrain for a seed. Pure: the same layout and seed give the same heights. */
export function createTerrain(layout: Layout, seed: number): Terrain {
  const T = layout.terrain;
  const layerSeeds = T.layers.map((l) => subSeed(seed, `terrain:${l.id}`));
  const wallSeed = subSeed(seed, "terrain:walls");
  const b = layout.bounds;
  const regions = layout.regions.map((r) => ({ r, box: shapeBox(r.shape), edge: r.edge ?? 8 }));
  const mask = (i: number, x: number, z: number): number => {
    const g = regions[i]!;
    if (boxDistance(g.box, x, z) > g.edge) return 0;
    return 1 - smoothstep(0, g.edge, shapeDistance(g.r.shape, x, z));
  };

  // 1, 2: noise, walls, landmarks
  const raw = (x: number, z: number): number => {
    let n = 0;
    T.layers.forEach((l, i) => void (n += l.amplitude * fbm(layerSeeds[i]!, x, z, l.scale, l.octaves ?? 1, l.ridged ?? false)));
    let rough = 1;
    regions.forEach((g, i) => {
      if (g.r.roughness === undefined) return;
      const m = mask(i, x, z);
      if (m > 0) rough = rough * (1 - m) + g.r.roughness * m;
    });
    let h = T.base + n * rough;
    if (T.walls) {
      const d = Math.min(x - b.x0, b.x1 - x, z - b.z0, b.z1 - z);
      const k = smoothstep(T.walls.inset + T.walls.width, T.walls.inset, d);
      if (k > 0) h += k * (T.walls.height + (T.walls.noise ?? 0) * fbm(wallSeed, x, z, 23, 2, true));
    }
    for (const l of layout.landmarks) {
      const d = Math.hypot(x - l.x, z - l.z) / l.radius;
      if (d >= 1) continue;
      const k = smoothstep(1, l.top ?? 0.4, d);
      h += (l.kind === "bowl" ? -1 : 1) * l.height * k;
    }
    return h;
  };

  // 3: flattened regions, each toward its centre's raw height plus its lift
  const flatTargets = regions.map((g) => {
    if (!g.r.flatten) return 0;
    const [cx, cz] = shapeCentre(g.r.shape);
    return raw(cx, cz) + (g.r.flatten.lift ?? 0);
  });
  const levelled = (x: number, z: number): number => {
    let h = raw(x, z);
    regions.forEach((g, i) => {
      if (!g.r.flatten) return;
      const m = mask(i, x, z);
      if (m > 0) h += (flatTargets[i]! - h) * m * g.r.flatten.strength;
    });
    return h;
  };

  // 4: paths, each toward its own line's (smoothed) height
  const paths = layout.paths.map((p) => ({ p, line: polyline(p.points), reach: p.width / 2 + SHOULDER }));
  const pathed = (x: number, z: number): number => {
    let h = levelled(x, z);
    for (const { p, line, reach } of paths) {
      if (boxDistance(line.box, x, z) > reach) continue;
      const near = nearestOn(line, x, z);
      if (near.d > reach) continue;
      const w = (p.flatten ?? 0.85) * (1 - smoothstep(p.width / 2, reach, near.d));
      let target = 0;
      for (const ds of [-PATH_SMOOTH, 0, PATH_SMOOTH]) {
        const [px, pz] = pointAlong(line, near.s + ds);
        target += levelled(px, pz) / 3;
      }
      h += (target - h) * w;
    }
    return h;
  };

  // 5: water: each stream's bank height sampled along its course, smoothed, and made to fall downstream
  const streams: { w: Water; line: Polyline; width: number; bank: number; bed: number[]; surface: number[] }[] = [];
  const ponds: { w: Water; x: number; z: number; r: number; bank: number; bed: number; surface: number }[] = [];
  for (const w of layout.water) {
    const fill = w.fill ?? 0.6;
    if (w.kind === "stream" && w.points) {
      const line = polyline(w.points);
      const n = Math.max(2, Math.ceil(line.length / STREAM_STEP) + 1);
      // the bank: the lowest of the ground on the course and at both banks' outer edges, so the water sits below the
      // ground on either side (a course across a slope would otherwise stand above its lower side)
      const side = (w.width ?? 2) / 2 + (w.bank ?? 2);
      const banks: number[] = [];
      for (let k = 0; k < n; k++) {
        let sum = 0;
        let cnt = 0;
        for (let ds = -STREAM_SMOOTH; ds <= STREAM_SMOOTH; ds += STREAM_SMOOTH / 2) {
          const s = (k / (n - 1)) * line.length + ds;
          const [px, pz] = pointAlong(line, s);
          const [qx, qz] = pointAlong(line, s + 0.5);
          const l = Math.hypot(qx - px, qz - pz) || 1;
          const nx = -(qz - pz) / l;
          const nz = (qx - px) / l;
          sum += Math.min(pathed(px, pz), pathed(px + nx * side, pz + nz * side), pathed(px - nx * side, pz - nz * side));
          cnt++;
        }
        banks.push(sum / cnt);
      }
      const bed: number[] = [];
      for (let k = 0; k < n; k++) bed.push(Math.min(k ? bed[k - 1]! : Infinity, banks[k]! - w.depth));
      streams.push({ w, line, width: w.width ?? 2, bank: w.bank ?? 2, bed, surface: bed.map((v) => v + w.depth * fill) });
    } else if (w.kind === "pond" && w.shape?.kind === "circle") {
      const s = w.shape;
      const bed = pathed(s.x, s.z) - w.depth;
      ponds.push({ w, x: s.x, z: s.z, r: s.r, bank: w.bank ?? 2, bed, surface: bed + w.depth * fill });
    }
  }
  const along = (st: (typeof streams)[number], arr: number[], s: number): number => {
    const f = (Math.min(st.line.length, Math.max(0, s)) / st.line.length) * (arr.length - 1);
    const i = Math.min(arr.length - 2, Math.floor(f));
    return arr[i]! + (arr[i + 1]! - arr[i]!) * (f - i);
  };

  const heightAt = (x: number, z: number): number => {
    let h = pathed(x, z);
    for (const st of streams) {
      const reach = st.width / 2 + st.bank;
      if (boxDistance(st.line.box, x, z) > reach) continue;
      const near = nearestOn(st.line, x, z);
      if (near.d > reach) continue;
      const bed = along(st, st.bed, near.s);
      const k = smoothstep(st.width * 0.25, reach, near.d);
      h = Math.min(h, bed + (h - bed) * k);
    }
    for (const p of ponds) {
      const d = Math.hypot(x - p.x, z - p.z);
      const reach = p.r + p.bank;
      if (d > reach) continue;
      const k = smoothstep(p.r * 0.3, reach, d);
      h = Math.min(h, p.bed + (h - p.bed) * k);
    }
    return h;
  };

  const slopeAt = (x: number, z: number): number => {
    const e = 0.5;
    const dx = (heightAt(x + e, z) - heightAt(x - e, z)) / (2 * e);
    const dz = (heightAt(x, z + e) - heightAt(x, z - e)) / (2 * e);
    return (Math.atan(Math.hypot(dx, dz)) * 180) / Math.PI;
  };

  const waterAt = (x: number, z: number): { id: string; d: number; surface: number } | null => {
    let best: { id: string; d: number; surface: number } | null = null;
    for (const st of streams) {
      if (boxDistance(st.line.box, x, z) > st.width / 2 + st.bank + 4) continue;
      const near = nearestOn(st.line, x, z);
      const d = near.d - st.width / 2;
      if (!best || d < best.d) best = { id: st.w.id, d, surface: along(st, st.surface, near.s) };
    }
    for (const p of ponds) {
      const d = Math.hypot(x - p.x, z - p.z) - p.r;
      if (!best || d < best.d) best = { id: p.w.id, d, surface: p.surface };
    }
    return best;
  };

  const pathAt = (x: number, z: number): { id: string; d: number } | null => {
    let best: { id: string; d: number } | null = null;
    for (const { p, line } of paths) {
      if (best && boxDistance(line.box, x, z) - p.width / 2 > best.d) continue;
      const d = nearestOn(line, x, z).d - p.width / 2;
      if (!best || d < best.d) best = { id: p.id, d };
    }
    return best;
  };

  const regionAt = (x: number, z: number): string | null => {
    for (let i = regions.length - 1; i >= 0; i--) {
      const g = regions[i]!;
      if (boxDistance(g.box, x, z) > 0) continue;
      if (shapeDistance(g.r.shape, x, z) <= 0) return g.r.id;
    }
    return null;
  };

  const surfaceAt = (x: number, z: number): string => {
    const w = waterAt(x, z);
    if (w) {
      const src = layout.water.find((v) => v.id === w.id)!;
      if (w.d <= 0) return src.bedSurface ?? "sand";
      if (w.d <= (src.bank ?? 2)) return src.bankSurface ?? "sand";
    }
    const p = pathAt(x, z);
    if (p && p.d <= 0) return layout.paths.find((v) => v.id === p.id)!.surface;
    if (T.cliff && slopeAt(x, z) > T.cliff.degrees) return T.cliff.surface;
    const r = regionAt(x, z);
    return r ? layout.regions.find((v) => v.id === r)!.surface : T.surface;
  };

  const courses: WaterCourse[] = [
    ...streams.map((st): StreamCourse => {
      const n = st.bed.length;
      return {
        id: st.w.id,
        kind: "stream",
        width: st.width,
        points: st.bed.map((bed, k) => {
          const [x, z] = pointAlong(st.line, (k / (n - 1)) * st.line.length);
          return { x, z, bed, surface: st.surface[k]! };
        }),
      };
    }),
    ...ponds.map((p): PondCourse => ({ id: p.w.id, kind: "pond", x: p.x, z: p.z, r: p.r, bed: p.bed, surface: p.surface })),
  ];

  return { heightAt, slopeAt, surfaceAt, regionAt, pathAt, waterAt, water: () => structuredClone(courses) };
}
